import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { breakpointValue, ifThen, otherwise } from '@cpangular/rxjs';
import { IfHasRenderInSourcesDirective, RenderInService } from 'common-ui';
import { ProductComponentStore } from 'products-data';
import { Observable } from 'rxjs';

import { PRODUCT_RESULT_VIEW_COMPONENT_TOKENS } from '../../../types/tokens';
import { ProductResultHostComponentBase } from '../ProductResultHostComponentBase';
import { ProductResultRenderTarget, ProductResultRenderTargetComponents } from './render-targets/ProductResultRenderTarget';
import { RenderTargetComponents } from './render-targets/RenderTargetComponents';

@Component({
  selector: 'ideal-product-result-layout',
  imports: [RouterModule, NgComponentOutlet, RenderTargetComponents, IfHasRenderInSourcesDirective],
  standalone: true,
  templateUrl: './product-result-layout.component.html',
  styleUrl: './product-result-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderInService, ProductComponentStore],
  host: {
    '[attr.layout]': 'layout()',
    '[attr.h-size]': 'componentWidth()',
    '[class.hide-info]': 'hideInfo()',
  },
})
export class ProductResultLayoutComponent extends ProductResultHostComponentBase {
  private readonly _renderInService = inject(RenderInService);
  private readonly _element = inject(ElementRef<HTMLElement>);

  protected readonly LayoutTargets = ProductResultRenderTarget;
  protected readonly LayoutComponents = ProductResultRenderTargetComponents;

  protected readonly componentWidthCard = toSignal(
    breakpointValue(
      this._element.nativeElement,
      ifThen((size) => size.width < 350, 'sm'),
      ifThen((size) => size.width < 450, 'md'),
      ifThen((size) => size.width < 800, 'lg'),
      otherwise('xl'),
    ) as Observable<'sm' | 'md' | 'lg' | 'xl'>,
    { initialValue: 'sm' },
  );

  protected readonly componentWidthRow = toSignal(
    breakpointValue(
      this._element.nativeElement,
      ifThen((size) => size.width < 350, 'sm'),
      ifThen((size) => size.width < 900, 'md'),
      ifThen((size) => size.width < 1200, 'lg'),
      otherwise('xl'),
    ) as Observable<'sm' | 'md' | 'lg' | 'xl'>,
    { initialValue: 'sm' },
  );

  protected readonly componentWidth = computed(() => (this.layout() === 'card' ? this.componentWidthCard() : this.componentWidthRow()));

  public readonly layout = input<'card' | 'row'>('card');

  protected readonly hasInfo = computed(() => {
    return this._renderInService.getSources(this.LayoutComponents[ProductResultRenderTarget.ImagesOverlay])().length > 0;
  });

  protected readonly hideInfo = computed(() => {
    if (this.layout() === 'row' && this.componentWidth() !== 'xl') {
      return true;
    }
    return false;
  });

  protected readonly productReady = computed(
    () => this.typeComponentInjector() && this.productTypeComponents() && !this.product.loading() && this.productId(),
  );

  protected readonly componentInputs = computed(() => ({
    productId: this.id(),
    layout: this.layout(),
  }));

  constructor() {
    super(PRODUCT_RESULT_VIEW_COMPONENT_TOKENS);
  }
}
